import {
  Alert,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { AddTokenFlow } from "./AddTokenFlow";
import { BotAccountsSection } from "./BotAccountsSection";
import { TokenCard } from "./TokenCard";
import { deleteTargets, keyedTokens, rolesWithLiveToken } from "./tokenStatus";
import { useRepoHostTokens } from "./useRepoHostTokens";

// Все известные роли, для которых можно добавить токен.
const KNOWN_ROLES = [
  "orchestrator",
  "coder",
  "reviewer",
  "writer",
  "infra",
] as const;

// Секция «Токены ролей» на `/settings/connections`.
// Показывает карточку на каждую строку истории токенов org и кнопки
// «Добавить» для ролей без живого токена.
export function TokensSection(): ReactElement {
  const { t } = useTranslation();
  const tokensQuery = useRepoHostTokens();

  const [addRole, setAddRole] = useState<string | null>(null);

  const handleChanged = (): void => {
    void tokensQuery.refetch();
  };

  if (tokensQuery.isPending) {
    return (
      <Stack gap="md">
        <Title order={3}>{t("connections.tokens.title")}</Title>
        <Loader />
      </Stack>
    );
  }

  if (tokensQuery.isError) {
    return (
      <Stack gap="md">
        <Title order={3}>{t("connections.tokens.title")}</Title>
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
      </Stack>
    );
  }

  const tokens = tokensQuery.data.tokens;

  const targets = deleteTargets(tokens);
  const liveRoles = rolesWithLiveToken(tokens);

  // «Добавить» доступно ролям без живого токена: после удаления или парковки
  // роль заводится заново.
  const missingRoles = KNOWN_ROLES.filter((role) => !liveRoles.has(role));

  return (
    <>
      <Stack gap="md">
        <Title order={3}>{t("connections.tokens.title")}</Title>

        {tokens.length === 0 ? (
          <Text c="dimmed">{t("connections.tokens.empty")}</Text>
        ) : (
          <Stack gap="sm">
            {keyedTokens(tokens).map(({ key, token }) => (
              <TokenCard
                key={key}
                token={token}
                deletable={targets.has(token)}
                onChanged={handleChanged}
              />
            ))}
          </Stack>
        )}

        {missingRoles.length > 0 ? (
          <Group gap="xs">
            {missingRoles.map((role) => (
              <Button
                key={role}
                size="xs"
                variant="default"
                onClick={() => {
                  setAddRole(role);
                }}
              >
                {t("connections.tokens.addButton")}: {role}
              </Button>
            ))}
          </Group>
        ) : null}

        {tokens.length > 0 ? (
          <BotAccountsSection tokens={tokens} onChanged={handleChanged} />
        ) : null}
      </Stack>

      {addRole !== null ? (
        <AddTokenFlow
          role={addRole}
          onClose={() => {
            setAddRole(null);
          }}
          onSuccess={handleChanged}
        />
      ) : null}
    </>
  );
}
